/* eslint-disable react/no-unknown-property -- Firebolt потребляет css-проп при сборке. */

// TEXT & VOICE CHAT — полноэкранный экран чата и голосового канала.
// Пропы: { world, onClose }
// ESC закрывает экран.

import { css } from '@firebolt-dev/css'
import { useEffect, useRef, useState, useCallback } from 'react'
import {
  MicIcon,
  MicOffIcon,
  SendHorizonalIcon,
  SmileIcon,
  PaperclipIcon,
  Volume2Icon,
  VolumeXIcon,
  XIcon,
  UsersIcon,
  MessageSquareTextIcon,
  MoreHorizontalIcon,
  ChevronRightIcon,
  AlertTriangleIcon,
} from 'lucide-react'

import { COLORS, RADII, space, MOTION, SHADOWS, GLASS, TYPE, ICON_SIZES, INPUT_HEIGHTS, TOUCH_MIN } from '../theme'
import {
  chatChannels,
  chatMessages,
  sendChat,
  peopleAround,
  playerCard,
  worldStatus,
  subscribeScreenData,
  chatPresence,
} from '../screen-data'

// ─── Вспомогательные функции ─────────────────────────────────────────────────

// Форматирует время сообщения: если сегодня — только HH:MM, иначе — дата
function formatTime(isoString) {
  if (!isoString) return ''
  try {
    const d = new Date(isoString)
    const now = new Date()
    const sameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    if (sameDay) return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
  } catch {
    return ''
  }
}

// Первые буквы имени для аватар-заглушки
function nameInitials(name) {
  const parts = String(name || '?').trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Цвет аватара по имени (детерминированный).
// Используем только токены из theme.js: базовые акценты + семантика.
function avatarColor(name) {
  const palette = [
    COLORS.cyan,        // бирюза
    COLORS.success,     // зелёный
    COLORS.warning,     // жёлтый
    COLORS.error,       // красный
    COLORS.focusRing,   // синий (#33b2ff)
    COLORS.cyanHover,   // светло-бирюза
    COLORS.cyanPressed, // тёмно-бирюза
  ]
  let hash = 0
  for (let i = 0; i < (name || '').length; i++) hash = (hash * 31 + name.charCodeAt(i)) & 0xffff
  return palette[hash % palette.length]
}

// ─── Главный компонент ────────────────────────────────────────────────────────

export function ChatScreen({ world, onClose, initialTargetUserId = '' }) {
  // --- Состояние каналов и сообщений ---
  const [activeChannel, setActiveChannel] = useState(initialTargetUserId ? 'direct' : 'nearby')
  const [channels, setChannels] = useState(() => chatChannels(world))
  const [msgs, setMsgs] = useState([])
  const [participants, setParticipants] = useState(() => peopleAround(world, { radius: 60 }))
  const [directTarget, setDirectTarget] = useState(String(initialTargetUserId || ''))
  const [directPlayers, setDirectPlayers] = useState(() => chatPresence(world))
  const [card] = useState(() => playerCard(world))
  const [status, setStatus] = useState(() => worldStatus(world))

  // --- Состояние ввода ---
  const [inputText, setInputText] = useState('')
  const maxLen = 200

  // --- Голос ---
  const [voice, setVoice] = useState(() => ({ ...world.livekit.status }))
  const [voiceLevel, setVoiceLevel] = useState(0) // 0–1, анимация уровня
  const voiceLevelTimer = useRef(null)

  // --- Уведомление о новом сообщении (когда скролл не в конце) ---
  const [newMsgToast, setNewMsgToast] = useState(null)
  const newMsgToastTimer = useRef(null)

  // --- Свёрнутый режим (правый нижний угол) ---
  const [collapsed, setCollapsed] = useState(false)

  // --- Мьют участников (локально, только аудио в LiveKit) ---
  const [mutedAll, setMutedAll] = useState(false)
  const [participantMutes, setParticipantMutes] = useState({})
  const [participantVolumes, setParticipantVolumes] = useState({})

  // --- Скролл ленты сообщений ---
  const feedRef = useRef(null)
  const feedBottomRef = useRef(null)
  const inputRef = useRef(null)
  const isAtBottom = useRef(true)

  // --- Подписка на чат ---
  useEffect(() => {
    const update = () => setMsgs(chatMessages(world))
    const offChat = world.chat.subscribe(update)
    const offScreen = subscribeScreenData(world, 'chat', update)
    return () => { offChat(); offScreen() }
  }, [world])

  useEffect(() => subscribeScreenData(world, 'chat', () => setDirectPlayers(chatPresence(world))), [world])

  // --- Каналы и участники: обновляем по таймеру ---
  useEffect(() => {
    const update = () => {
      setChannels(chatChannels(world))
      setParticipants(peopleAround(world, { radius: 60 }))
      setStatus(worldStatus(world))
    }
    update()
    const timer = setInterval(update, 1500)
    return () => clearInterval(timer)
  }, [world])

  // --- Голос: статус и уровень ---
  useEffect(() => {
    const onStatus = status => setVoice({ ...status })
    world.livekit.on('status', onStatus)
    return () => world.livekit.off('status', onStatus)
  }, [world])

  // Уровень берём только из текущего LiveKit participant; без синтетической анимации.
  useEffect(() => {
    if (voice.speaking) {
      voiceLevelTimer.current = setInterval(() => {
        setVoiceLevel(Math.max(0, Math.min(1, Number(world.livekit?.room?.localParticipant?.audioLevel) || 0)))
      }, 120)
    } else {
      clearInterval(voiceLevelTimer.current)
      setVoiceLevel(0)
    }
    return () => clearInterval(voiceLevelTimer.current)
  }, [voice.speaking, world])

  // --- Автоскролл к последнему сообщению ---
  useEffect(() => {
    if (!feedRef.current) return
    if (isAtBottom.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight
    } else if (msgs.length > 0) {
      // Показываем тост о новом сообщении
      const last = msgs[msgs.length - 1]
      if (last && last.fromId !== world.network?.id) {
        setNewMsgToast(last)
        clearTimeout(newMsgToastTimer.current)
        newMsgToastTimer.current = setTimeout(() => setNewMsgToast(null), 4000)
      }
    }
  }, [msgs, world.network?.id])

  // Слежение: у нижнего края или нет
  const onFeedScroll = useCallback(() => {
    if (!feedRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = feedRef.current
    isAtBottom.current = scrollTop + clientHeight >= scrollHeight - 40
    if (isAtBottom.current) setNewMsgToast(null)
  }, [])

  // --- ESC закрывает ---
  useEffect(() => {
    const onKey = event => {
      if (event.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // --- Отправка сообщения ---
  const handleSend = useCallback(() => {
    const text = inputText.trim()
    if (!text) return
    const channel = channels.find(item => item.id === activeChannel)
    if (!channel?.available || activeChannel === 'system') return
    if (!sendChat(world, text, { channel: activeChannel, targetUserId: activeChannel === 'direct' ? directTarget : null })) return
    setInputText('')
    // Скроллим вниз после отправки
    setTimeout(() => {
      if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight
    }, 50)
  }, [activeChannel, channels, directTarget, inputText, world])

  const onInputKeyDown = useCallback(
    event => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        handleSend()
      }
    },
    [handleSend]
  )

  // --- Мьют конкретного участника (локально) ---
  const toggleParticipantMute = useCallback(id => {
    setParticipantMutes(prev => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const toggleMutedAll = useCallback(() => {
    setMutedAll(v => !v)
  }, [])

  // --- Свёрнутый режим ---
  if (collapsed) {
    const unread = msgs.filter(m => m.fromId && m.fromId !== world.network?.id).length
    return (
      <CollapsedChat
        status={status}
        unread={unread}
        voice={voice}
        world={world}
        onExpand={() => setCollapsed(false)}
        onClose={onClose}
      />
    )
  }

  // --- Идентификатор текущего игрока ---
  const myId = card?.id || world.entities?.player?.data?.id || world.network?.id

  // --- Фильтрация сообщений по каналу ---
  const filteredMsgs = filterMsgsByChannel(msgs, activeChannel)

  return (
    <div
      role='dialog'
      aria-label='Чат и голос'
      css={css`
        /* Перекрываем мир — полноэкранный оверлей */
        position: fixed;
        inset: 0;
        z-index: 50;
        background: rgba(3, 7, 13, 0.88);
        backdrop-filter: ${GLASS.base};
        display: grid;
        /* 3 колонки: каналы | лента | участники */
        grid-template-columns: 220px 1fr 260px;
        grid-template-rows: 1fr;
        gap: 0;
        font-family: inherit;
        color: ${COLORS.text};

        /* ── Мобильный: < 900px — колонки схлопываются ── */
        @media (max-width: 900px) {
          grid-template-columns: 1fr;
          grid-template-rows: auto 1fr auto;
          overflow: hidden;
        }
      `}
    >
      {/* ── ЛЕВАЯ КОЛОНКА: КАНАЛЫ ─────────────────────────────────────── */}
      <ChannelsSidebar
        channels={channels}
        activeChannel={activeChannel}
        status={status}
        onSelect={setActiveChannel}
      />

      {/* ── ЦЕНТР: ЛЕНТА СООБЩЕНИЙ + ВВОД ───────────────────────────── */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          border-left: 1px solid ${COLORS.border};
          border-right: 1px solid ${COLORS.border};
          min-height: 0;
          overflow: hidden;

          @media (max-width: 900px) {
            border-left: none;
            border-right: none;
            border-top: 1px solid ${COLORS.border};
            border-bottom: 1px solid ${COLORS.border};
          }
        `}
      >
        {/* Шапка центральной колонки */}
        <div
          css={css`
            flex: 0 0 auto;
            display: flex;
            align-items: center;
            gap: ${space(1)};
            padding: ${space(1)} ${space(2)};
            border-bottom: 1px solid ${COLORS.border};
            background: ${COLORS.bgPanel};
          `}
        >
          <MessageSquareTextIcon size={ICON_SIZES.sm} color={COLORS.cyan} />
          <span
            css={css`
              font-size: ${TYPE.title.size};
              font-weight: ${TYPE.title.weight};
              letter-spacing: ${TYPE.title.spacing};
            `}
          >
            {channels.find(c => c.id === activeChannel)?.name || 'ЧАТ'}
          </span>
          {channels.find(c => c.id === activeChannel)?.hint && (
            <span
              css={css`
                font-size: ${TYPE.caption.size};
                color: ${COLORS.textSecondary};
                margin-left: ${space(0)};
              `}
            >
              {channels.find(c => c.id === activeChannel).hint}
            </span>
          )}
          {activeChannel === 'direct' && (
            <select aria-label='Адресат личного сообщения' value={directTarget} onChange={event => setDirectTarget(event.target.value)} css={css`min-width:150px;background:${COLORS.bgElevated};border:1px solid ${COLORS.border};border-radius:${RADII.md};color:${COLORS.text};padding:7px;`}>
              <option value=''>ВЫБЕРИ ИГРОКА</option>
              {directPlayers.map(person => <option key={person.userId} value={person.userId}>{person.name}</option>)}
            </select>
          )}
          {/* Кнопка свернуть (только мобайл + дескток) */}
          <button
            type='button'
            aria-label='Свернуть чат'
            onClick={() => setCollapsed(true)}
            css={css`
              margin-left: auto;
              appearance: none;
              background: transparent;
              border: 1px solid ${COLORS.border};
              border-radius: ${RADII.md};
              color: ${COLORS.textSecondary};
              width: ${TOUCH_MIN}px;
              height: 32px;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              transition: background ${MOTION.hover}, color ${MOTION.hover};
              &:hover {
                background: ${COLORS.cyanSoft};
                color: ${COLORS.cyan};
              }
            `}
          >
            <ChevronRightIcon size={ICON_SIZES.xs} />
          </button>
          {/* Кнопка закрыть */}
          <button
            type='button'
            aria-label='Закрыть чат'
            onClick={onClose}
            css={css`
              appearance: none;
              background: transparent;
              border: 1px solid ${COLORS.border};
              border-radius: ${RADII.md};
              color: ${COLORS.textSecondary};
              width: ${TOUCH_MIN}px;
              height: 32px;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              transition: background ${MOTION.hover}, color ${MOTION.hover};
              &:hover {
                background: ${COLORS.errorSoft};
                color: ${COLORS.error};
              }
            `}
          >
            <XIcon size={ICON_SIZES.xs} />
          </button>
        </div>

        {/* Лента сообщений */}
        <div
          ref={feedRef}
          onScroll={onFeedScroll}
          css={css`
            flex: 1;
            min-height: 0;
            overflow-y: auto;
            padding: ${space(2)};
            display: flex;
            flex-direction: column;
            gap: ${space(1)};
            scroll-behavior: smooth;
            /* Скрытый скроллбар — стиль в духе HUD */
            scrollbar-width: thin;
            scrollbar-color: ${COLORS.border} transparent;
            &::-webkit-scrollbar {
              width: 4px;
            }
            &::-webkit-scrollbar-thumb {
              background: ${COLORS.border};
              border-radius: ${RADII.round};
            }
          `}
        >
          {filteredMsgs.length === 0 ? (
            <EmptyState channel={activeChannel} />
          ) : (
            filteredMsgs.map(msg => (
              <MessageBubble
                key={msg.id}
                msg={msg}
                isMine={msg.fromId === myId}
              />
            ))
          )}
          <div ref={feedBottomRef} />
        </div>

        {/* Поле ввода */}
        <InputArea
          value={inputText}
          maxLen={maxLen}
          onChange={setInputText}
          onSend={handleSend}
          onKeyDown={onInputKeyDown}
          voice={voice}
          voiceLevel={voiceLevel}
          world={world}
          inputRef={inputRef}
          disabled={activeChannel === 'system' || !channels.find(item => item.id === activeChannel)?.available || (activeChannel === 'direct' && !directTarget)}
        />
      </div>

      {/* ── ПРАВАЯ КОЛОНКА: УЧАСТНИКИ ────────────────────────────────── */}
      <ParticipantsPanel
        participants={participants}
        mutedAll={mutedAll}
        participantMutes={participantMutes}
        participantVolumes={participantVolumes}
        onToggleMutedAll={toggleMutedAll}
        onToggleParticipantMute={toggleParticipantMute}
        onVolumeChange={(id, vol) =>
          setParticipantVolumes(prev => ({ ...prev, [id]: vol }))
        }
      />

      {/* ── ТОСТ О НОВОМ СООБЩЕНИИ (слева снизу) ────────────────────── */}
      {newMsgToast && (
        <NewMessageToast
          msg={newMsgToast}
          onJump={() => {
            if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight
            setNewMsgToast(null)
          }}
          onDismiss={() => setNewMsgToast(null)}
        />
      )}
    </div>
  )
}

// ─── Фильтрация сообщений по каналу ──────────────────────────────────────────

function filterMsgsByChannel(msgs, channel) {
  if (!msgs.length) return []
  switch (channel) {
    case 'system':
      // Системные — сообщения без автора
      return msgs.filter(m => m.system)
    case 'nearby':
    case 'world':
      return msgs.filter(m => m.channel === channel)
    case 'party':
    case 'direct':
      return msgs.filter(m => m.channel === channel)
    default:
      return msgs
  }
}

// ─── ЛЕВАЯ КОЛОНКА: Каналы ──────────────────────────────────────────────────

function ChannelsSidebar({ channels, activeChannel, status, onSelect }) {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        background: ${COLORS.bgPanel};
        border-right: 1px solid ${COLORS.border};
        overflow: hidden;

        @media (max-width: 900px) {
          flex-direction: row;
          overflow-x: auto;
          border-right: none;
          padding: ${space(1)} ${space(1)} 0;
          scrollbar-width: none;
          &::-webkit-scrollbar { display: none; }
        }
      `}
    >
      {/* Шапка панели каналов */}
      <div
        css={css`
          flex: 0 0 auto;
          padding: ${space(2)};
          border-bottom: 1px solid ${COLORS.border};

          @media (max-width: 900px) {
            display: none;
          }
        `}
      >
        <div
          css={css`
            font-size: ${TYPE.section.size};
            font-weight: ${TYPE.section.weight};
            letter-spacing: ${TYPE.section.spacing};
            color: ${COLORS.textSecondary};
            margin-bottom: ${space(1)};
          `}
        >
          КАНАЛЫ
        </div>
        {/* Название места и онлайн */}
        <div
          css={css`
            font-size: ${TYPE.caption.size};
            color: ${COLORS.textSecondary};
            display: flex;
            align-items: center;
            gap: ${space(0)};
          `}
        >
          <span
            css={css`
              width: 6px;
              height: 6px;
              border-radius: ${RADII.round};
              background: ${COLORS.success};
              flex: 0 0 auto;
            `}
          />
          {status.place} · {status.online} онлайн
        </div>
      </div>

      {/* Список каналов */}
      <nav
        aria-label='Каналы чата'
        css={css`
          flex: 1;
          overflow-y: auto;
          padding: ${space(1)} 0;
          display: flex;
          flex-direction: column;
          gap: 2px;

          @media (max-width: 900px) {
            flex-direction: row;
            padding: 0;
            overflow-y: visible;
            overflow-x: visible;
            flex: 0 0 auto;
          }
        `}
      >
        {channels.map(channel => (
          <ChannelItem
            key={channel.id}
            channel={channel}
            active={channel.id === activeChannel}
            onSelect={onSelect}
          />
        ))}
      </nav>
    </div>
  )
}

function ChannelItem({ channel, active, onSelect }) {
  return (
    <button
      type='button'
      aria-pressed={active}
      aria-label={`Канал ${channel.name}`}
      onClick={() => channel.available && onSelect(channel.id)}
      disabled={!channel.available}
      css={css`
        appearance: none;
        background: ${active ? COLORS.cyanSoft : 'transparent'};
        border: none;
        border-left: 3px solid ${active ? COLORS.cyan : 'transparent'};
        color: ${active ? COLORS.cyan : COLORS.text};
        padding: ${space(1)} ${space(2)};
        text-align: left;
        cursor: ${channel.available ? 'pointer' : 'not-allowed'};
        opacity: ${channel.available ? 1 : .45};
        display: flex;
        align-items: center;
        gap: ${space(1)};
        font: inherit;
        font-size: ${TYPE.body.size};
        font-weight: ${active ? 650 : 500};
        letter-spacing: 0.08em;
        min-height: ${TOUCH_MIN}px;
        transition: background ${MOTION.hover}, color ${MOTION.hover}, border-color ${MOTION.hover};
        border-radius: 0 ${RADII.md} ${RADII.md} 0;

        /* Активный — рамка и подсветка */
        ${active
          ? `
          box-shadow: inset 0 0 0 1px ${COLORS.borderStrong};
          background: ${COLORS.cyanSoft};
        `
          : `
          &:hover {
            background: rgba(230, 234, 240, 0.04);
            color: ${COLORS.text};
          }
        `}

        @media (max-width: 900px) {
          border-left: none;
          border-bottom: 3px solid ${active ? COLORS.cyan : 'transparent'};
          border-radius: ${RADII.md} ${RADII.md} 0 0;
          padding: ${space(0)} ${space(1)};
          white-space: nowrap;
          min-height: ${TOUCH_MIN}px;
          flex: 0 0 auto;
        }
      `}
    >
      <span css={css`flex: 1;`}>{channel.name}</span>
      {channel.count > 0 && (
        <span
          css={css`
            background: ${active ? COLORS.cyan : COLORS.bgElevated};
            color: ${active ? COLORS.bgBase : COLORS.textSecondary};
            font-size: ${TYPE.caption.size};
            font-weight: 700;
            padding: 1px 6px;
            border-radius: ${RADII.round};
            min-width: 18px;
            text-align: center;
          `}
        >
          {channel.count}
        </span>
      )}
    </button>
  )
}

// ─── Пустое состояние ────────────────────────────────────────────────────────

function EmptyState({ channel }) {
  const labels = {
    nearby: 'Рядом пока тихо',
    world: 'Мир молчит',
    party: 'Группа не создана',
    direct: 'Личных сообщений нет',
    system: 'Нет системных событий',
  }
  const hints = {
    party: 'Создай группу командой /group create <имя>',
    direct: 'Напиши личное сообщение: /im <игрок> <текст>',
  }
  return (
    <div
      css={css`
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: ${space(1)};
        color: ${COLORS.textSecondary};
        padding: ${space(4)};
        text-align: center;
      `}
    >
      <MessageSquareTextIcon size={ICON_SIZES.lg} color={COLORS.border} />
      <div
        css={css`
          font-size: ${TYPE.body.size};
          font-weight: 500;
        `}
      >
        {labels[channel] || 'Нет сообщений'}
      </div>
      {hints[channel] && (
        <div
          css={css`
            font-size: ${TYPE.caption.size};
            color: ${COLORS.textDisabled};
            max-width: 240px;
          `}
        >
          {hints[channel]}
        </div>
      )}
    </div>
  )
}

// ─── ПУЗЫРЬ СООБЩЕНИЯ ─────────────────────────────────────────────────────────

function MessageBubble({ msg, isMine }) {
  const color = avatarColor(msg.from || 'system')
  const initials = nameInitials(msg.from || 'SYS')

  return (
    <div
      css={css`
        display: flex;
        gap: ${space(1)};
        align-items: flex-start;
        ${isMine ? 'flex-direction: row-reverse;' : ''}
      `}
    >
      {/* Аватар-кружок */}
      {!msg.system && (
        <div
          aria-hidden='true'
          css={css`
            flex: 0 0 auto;
            width: 32px;
            height: 32px;
            border-radius: ${RADII.round};
            background: ${color}22;
            border: 1.5px solid ${color}88;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.65rem;
            font-weight: 700;
            color: ${color};
          `}
        >
          {initials}
        </div>
      )}

      {/* Тело сообщения */}
      <div
        css={css`
          flex: 1;
          min-width: 0;
          max-width: 72%;
          ${isMine ? 'align-items: flex-end;' : ''}
          display: flex;
          flex-direction: column;
          gap: 3px;
        `}
      >
        {/* Имя и время */}
        {!msg.system && (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: ${space(0)};
              ${isMine ? 'flex-direction: row-reverse;' : ''}
            `}
          >
            <span
              css={css`
                font-size: ${TYPE.caption.size};
                font-weight: 650;
                color: ${isMine ? COLORS.cyan : color};
                display: flex;
                align-items: center;
                gap: 4px;
              `}
            >
              {/* Статус-точка: онлайн */}
              <span
                css={css`
                  width: 5px;
                  height: 5px;
                  border-radius: ${RADII.round};
                  background: ${COLORS.success};
                  display: inline-block;
                `}
              />
              {msg.from || 'Аноним'}
            </span>
            <span
              css={css`
                font-size: 0.65rem;
                color: ${COLORS.textDisabled};
              `}
            >
              {formatTime(msg.createdAt)}
            </span>
          </div>
        )}

        {/* Текст */}
        <div
          css={css`
            padding: ${space(1)} ${space(1)};
            border-radius: ${isMine ? `${RADII.lg} ${RADII.sm} ${RADII.lg} ${RADII.lg}` : `${RADII.sm} ${RADII.lg} ${RADII.lg} ${RADII.lg}`};
            background: ${msg.system
              ? COLORS.warningSoft
              : isMine
                ? COLORS.cyanSoft
                : COLORS.bgPanelLight};
            border: 1px solid ${msg.system
              ? COLORS.warning + '44'
              : isMine
                ? COLORS.borderStrong
                : COLORS.border};
            font-size: ${TYPE.body.size};
            line-height: 1.45;
            word-break: break-word;
            color: ${msg.system ? COLORS.warning : COLORS.text};
          `}
        >
          {msg.body}
        </div>

        {/* Строка действий под сообщением */}
        <MessageActions isMine={isMine} />
      </div>
    </div>
  )
}

// Реакции и действия под сообщением.
// Реакции и перевод — заглушка (мир не поддерживает): кнопки disabled с подсказкой «скоро».
function MessageActions({ isMine }) {
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: ${space(0)};
        ${isMine ? 'flex-direction: row-reverse;' : ''}
        flex-wrap: wrap;
      `}
    >
      {/* Реакции — disabled, tooltip «скоро» */}
      {['👍', '❤️', '😂'].map(emoji => (
        <button
          key={emoji}
          type='button'
          disabled
          aria-label={`Реакция ${emoji} (скоро)`}
          title='Реакции появятся скоро'
          css={css`
            appearance: none;
            background: ${COLORS.bgElevated};
            border: 1px solid ${COLORS.border};
            border-radius: ${RADII.pill};
            color: ${COLORS.textDisabled};
            font-size: 0.62rem;
            padding: 1px 6px;
            cursor: not-allowed;
            opacity: 0.5;
            display: flex;
            align-items: center;
            gap: 3px;
          `}
        >
          {emoji} <span>0</span>
        </button>
      ))}

      {/* Ответить */}
      <button
        type='button'
        disabled
        aria-label='Ответить (скоро)'
        title='Ответы появятся скоро'
        css={css`
          appearance: none;
          background: transparent;
          border: none;
          color: ${COLORS.textDisabled};
          font: inherit;
          font-size: 0.62rem;
          letter-spacing: 0.06em;
          cursor: not-allowed;
          opacity: 0.5;
          padding: 1px 4px;
        `}
      >
        Ответить
      </button>

      {/* Перевести */}
      <button
        type='button'
        disabled
        aria-label='Перевести (скоро)'
        title='Перевод появится скоро'
        css={css`
          appearance: none;
          background: transparent;
          border: none;
          color: ${COLORS.textDisabled};
          font: inherit;
          font-size: 0.62rem;
          letter-spacing: 0.06em;
          cursor: not-allowed;
          opacity: 0.5;
          padding: 1px 4px;
        `}
      >
        Перевести
      </button>
    </div>
  )
}

// ─── ПОЛЕ ВВОДА ────────────────────────────────────────────────────────────────

function InputArea({ value, maxLen, onChange, onSend, onKeyDown, voice, voiceLevel, world, inputRef, disabled = false }) {
  const remaining = maxLen - value.length
  const overLimit = remaining < 0

  // HOLD TO TALK — микрофон через LiveKit, если доступен
  const [holding, setHolding] = useState(false)
  const holdTimer = useRef(null)

  const startHold = () => {
    if (!voice.available) return
    setHolding(true)
    // Включаем микрофон
    if (!voice.mic || voice.muted) world.livekit.setMicrophoneEnabled(true)
  }
  const endHold = () => {
    setHolding(false)
    clearTimeout(holdTimer.current)
  }

  return (
    <div
      css={css`
        flex: 0 0 auto;
        border-top: 1px solid ${COLORS.border};
        background: ${COLORS.bgPanel};
        padding: ${space(1)} ${space(2)};
        display: flex;
        flex-direction: column;
        gap: ${space(0)};
      `}
    >
      {/* Основной ряд: кнопки + поле + счётчик + отправить */}
      <div
        css={css`
          display: flex;
          align-items: flex-end;
          gap: ${space(1)};
        `}
      >
        {/* HOLD TO TALK — микрофон */}
        <button
          type='button'
          aria-label={voice.available ? 'Нажми и держи для голоса' : 'Голос недоступен'}
          title={voice.available ? 'HOLD TO TALK' : 'Подключи голос в настройках'}
          onPointerDown={startHold}
          onPointerUp={endHold}
          onPointerLeave={endHold}
          disabled={!voice.available}
          css={css`
            appearance: none;
            flex: 0 0 auto;
            width: ${TOUCH_MIN}px;
            height: ${INPUT_HEIGHTS.medium}px;
            border-radius: ${RADII.md};
            border: 1px solid ${holding ? COLORS.cyan : COLORS.border};
            background: ${holding ? COLORS.cyanSoft : COLORS.bgElevated};
            color: ${voice.available
              ? holding
                ? COLORS.cyan
                : COLORS.textSecondary
              : COLORS.textDisabled};
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: ${voice.available ? 'pointer' : 'not-allowed'};
            transition: background ${MOTION.hover}, border-color ${MOTION.hover}, color ${MOTION.hover};
            box-shadow: ${holding ? SHADOWS.glowCyan : 'none'};
            opacity: ${voice.available ? 1 : 0.45};
          `}
        >
          {voice.muted || !voice.available ? (
            <MicOffIcon size={ICON_SIZES.sm} />
          ) : (
            <MicIcon size={ICON_SIZES.sm} />
          )}
        </button>

        {/* Поле ввода */}
        <div
          css={css`
            flex: 1;
            position: relative;
          `}
        >
          <textarea
            ref={inputRef}
            value={value}
            onChange={event => onChange(event.target.value.slice(0, maxLen + 10))}
            onKeyDown={onKeyDown}
            placeholder={disabled ? 'Отправка в этот канал недоступна' : 'Написать в чат…'}
            aria-label='Поле ввода сообщения'
            rows={1}
            maxLength={maxLen + 10}
            disabled={disabled}
            css={css`
              width: 100%;
              min-height: ${INPUT_HEIGHTS.medium}px;
              max-height: 100px;
              resize: none;
              padding: 10px ${space(1)};
              background: ${COLORS.bgElevated};
              border: 1px solid ${COLORS.border};
              border-radius: ${RADII.md};
              color: ${COLORS.text};
              font: inherit;
              /* 16px предотвращает зум на iOS при фокусе */
              font-size: 1rem;
              line-height: 1.4;
              outline: none;
              transition: border-color ${MOTION.hover};
              &:focus {
                border-color: ${COLORS.borderStrong};
                box-shadow: 0 0 0 2px ${COLORS.cyanSoft};
              }
              &::placeholder {
                color: ${COLORS.textDisabled};
              }
              scrollbar-width: thin;
              scrollbar-color: ${COLORS.border} transparent;
            `}
          />
          {/* Счётчик символов */}
          <span
            css={css`
              position: absolute;
              right: ${space(1)};
              bottom: 6px;
              font-size: 0.6rem;
              color: ${overLimit ? COLORS.error : COLORS.textDisabled};
              pointer-events: none;
            `}
          >
            {value.length}/{maxLen}
          </span>
        </div>

        {/* Вспомогательные кнопки */}
        <div
          css={css`
            flex: 0 0 auto;
            display: flex;
            align-items: flex-end;
            gap: ${space(0)};
          `}
        >
          {/* Эмодзи — disabled, скоро */}
          <button
            type='button'
            disabled
            aria-label='Эмодзи (скоро)'
            title='Эмодзи появятся скоро'
            css={css`
              appearance: none;
              width: ${TOUCH_MIN}px;
              height: ${INPUT_HEIGHTS.medium}px;
              border: 1px solid ${COLORS.border};
              border-radius: ${RADII.md};
              background: ${COLORS.bgElevated};
              color: ${COLORS.textDisabled};
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: not-allowed;
              opacity: 0.45;
            `}
          >
            <SmileIcon size={ICON_SIZES.sm} />
          </button>

          {/* Вложение — disabled, скоро */}
          <button
            type='button'
            disabled
            aria-label='Вложение (скоро)'
            title='Вложения появятся скоро'
            css={css`
              appearance: none;
              width: ${TOUCH_MIN}px;
              height: ${INPUT_HEIGHTS.medium}px;
              border: 1px solid ${COLORS.border};
              border-radius: ${RADII.md};
              background: ${COLORS.bgElevated};
              color: ${COLORS.textDisabled};
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: not-allowed;
              opacity: 0.45;
            `}
          >
            <PaperclipIcon size={ICON_SIZES.sm} />
          </button>

          {/* Отправить */}
          <button
            type='button'
            aria-label='Отправить сообщение'
            onClick={onSend}
            disabled={disabled || !value.trim() || overLimit}
            css={css`
              appearance: none;
              width: ${TOUCH_MIN}px;
              height: ${INPUT_HEIGHTS.medium}px;
              border: 1px solid ${!disabled && value.trim() && !overLimit ? COLORS.borderStrong : COLORS.border};
              border-radius: ${RADII.md};
              background: ${!disabled && value.trim() && !overLimit ? COLORS.cyanSoft : COLORS.bgElevated};
              color: ${!disabled && value.trim() && !overLimit ? COLORS.cyan : COLORS.textDisabled};
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: ${!disabled && value.trim() && !overLimit ? 'pointer' : 'not-allowed'};
              transition: background ${MOTION.hover}, border-color ${MOTION.hover}, color ${MOTION.hover};
              &:not(:disabled):hover {
                background: ${COLORS.cyan};
                color: ${COLORS.bgBase};
                box-shadow: ${SHADOWS.glowCyan};
              }
            `}
          >
            <SendHorizonalIcon size={ICON_SIZES.sm} />
          </button>
        </div>
      </div>

      {/* Полоска уровня голоса */}
      <VoiceLevelBar level={voiceLevel} speaking={voice.speaking} />
    </div>
  )
}

// Полоска уровня голоса под полем ввода
function VoiceLevelBar({ level, speaking }) {
  return (
    <div
      aria-label='Уровень голоса'
      css={css`
        height: 3px;
        border-radius: ${RADII.round};
        background: ${COLORS.border};
        overflow: hidden;
        opacity: ${speaking ? 1 : 0.35};
        transition: opacity ${MOTION.enter};
      `}
    >
      <div
        css={css`
          height: 100%;
          border-radius: ${RADII.round};
          background: ${COLORS.cyan};
          box-shadow: 0 0 6px ${COLORS.cyanGlow};
          transition: width 0.1s ease-out;
        `}
        style={{ width: `${Math.round(level * 100)}%` }}
      />
    </div>
  )
}

// ─── ПРАВАЯ ПАНЕЛЬ: УЧАСТНИКИ ─────────────────────────────────────────────────

function ParticipantsPanel({
  participants,
  mutedAll,
  participantMutes,
  participantVolumes,
  onToggleMutedAll,
  onToggleParticipantMute,
  onVolumeChange,
}) {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        background: ${COLORS.bgPanel};
        overflow: hidden;

        @media (max-width: 900px) {
          /* На мобайле панель участников прячется */
          display: none;
        }
      `}
    >
      {/* Шапка */}
      <div
        css={css`
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          gap: ${space(1)};
          padding: ${space(2)};
          border-bottom: 1px solid ${COLORS.border};
        `}
      >
        <UsersIcon size={ICON_SIZES.sm} color={COLORS.cyan} />
        <span
          css={css`
            font-size: ${TYPE.section.size};
            font-weight: ${TYPE.section.weight};
            letter-spacing: ${TYPE.section.spacing};
            color: ${COLORS.textSecondary};
            flex: 1;
          `}
        >
          УЧАСТНИКИ
        </span>
        <span
          css={css`
            font-size: ${TYPE.caption.size};
            color: ${COLORS.cyan};
            font-weight: 700;
          `}
        >
          {participants.length}
        </span>
      </div>

      {/* Список */}
      <div
        css={css`
          flex: 1;
          overflow-y: auto;
          padding: ${space(1)} 0;
          scrollbar-width: thin;
          scrollbar-color: ${COLORS.border} transparent;
        `}
      >
        {participants.length === 0 ? (
          <div
            css={css`
              padding: ${space(2)};
              color: ${COLORS.textDisabled};
              font-size: ${TYPE.caption.size};
              text-align: center;
            `}
          >
            Рядом никого нет
          </div>
        ) : (
          participants.map(p => (
            <ParticipantRow
              key={p.id || p.name}
              participant={p}
              muted={mutedAll || participantMutes[p.id]}
              volume={participantVolumes[p.id] ?? 100}
              onToggleMute={() => onToggleParticipantMute(p.id)}
              onVolumeChange={vol => onVolumeChange(p.id, vol)}
            />
          ))
        )}
      </div>

      {/* Нижние действия */}
      <div
        css={css`
          flex: 0 0 auto;
          border-top: 1px solid ${COLORS.border};
          padding: ${space(1)};
          display: flex;
          flex-direction: column;
          gap: ${space(0)};
        `}
      >
        {/* ЗАГЛУШИТЬ ВСЕХ */}
        <button
          type='button'
          onClick={onToggleMutedAll}
          aria-pressed={mutedAll}
          css={css`
            appearance: none;
            width: 100%;
            min-height: ${TOUCH_MIN}px;
            padding: 0 ${space(1)};
            border-radius: ${RADII.md};
            border: 1px solid ${mutedAll ? COLORS.borderStrong : COLORS.border};
            background: ${mutedAll ? COLORS.cyanSoft : 'transparent'};
            color: ${mutedAll ? COLORS.cyan : COLORS.textSecondary};
            font: inherit;
            font-size: ${TYPE.caption.size};
            font-weight: 650;
            letter-spacing: 0.12em;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: ${space(0)};
            cursor: pointer;
            transition: background ${MOTION.hover}, color ${MOTION.hover}, border-color ${MOTION.hover};
            &:hover {
              background: ${COLORS.cyanSoft};
              color: ${COLORS.cyan};
            }
          `}
        >
          {mutedAll ? <Volume2Icon size={ICON_SIZES.xs} /> : <VolumeXIcon size={ICON_SIZES.xs} />}
          {mutedAll ? 'ВКЛЮЧИТЬ ВСЕХ' : 'ЗАГЛУШИТЬ ВСЕХ'}
        </button>

        {/* ПОЖАЛОВАТЬСЯ НА КАНАЛ — destructive */}
        <button
          type='button'
          disabled
          aria-label='Пожаловаться на канал (скоро)'
          title='Жалобы на канал появятся скоро'
          css={css`
            appearance: none;
            width: 100%;
            min-height: ${TOUCH_MIN}px;
            padding: 0 ${space(1)};
            border-radius: ${RADII.md};
            border: 1px solid ${COLORS.error}44;
            background: ${COLORS.errorSoft};
            color: ${COLORS.error};
            font: inherit;
            font-size: ${TYPE.caption.size};
            font-weight: 650;
            letter-spacing: 0.12em;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: ${space(0)};
            cursor: not-allowed;
            opacity: 0.55;
          `}
        >
          <AlertTriangleIcon size={ICON_SIZES.xs} />
          ПОЖАЛОВАТЬСЯ НА КАНАЛ
        </button>
      </div>
    </div>
  )
}

// Строка участника
function ParticipantRow({ participant, muted, volume, onToggleMute, onVolumeChange }) {
  const color = avatarColor(participant.name)
  const initials = nameInitials(participant.name)
  const isAI = participant.isAI

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: ${space(1)};
        padding: ${space(0)} ${space(2)};
        min-height: ${TOUCH_MIN}px;
        border-left: 2px solid ${isAI ? COLORS.cyan : 'transparent'};
        background: ${isAI ? COLORS.cyanSoft : 'transparent'};
        transition: background ${MOTION.hover};
        &:hover {
          background: rgba(230, 234, 240, 0.03);
        }
      `}
    >
      {/* Аватар */}
      <div
        aria-hidden='true'
        css={css`
          flex: 0 0 auto;
          width: 28px;
          height: 28px;
          border-radius: ${RADII.round};
          background: ${color}22;
          border: 1.5px solid ${color}88;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.55rem;
          font-weight: 700;
          color: ${color};
        `}
      >
        {initials}
      </div>

      {/* Имя и метка */}
      <div
        css={css`
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 1px;
        `}
      >
        <span
          css={css`
            font-size: ${TYPE.caption.size};
            font-weight: 600;
            color: ${COLORS.text};
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          `}
        >
          {participant.name}
        </span>
        {/* Языковой бейдж и дистанция */}
        <span
          css={css`
            font-size: 0.6rem;
            color: ${COLORS.textDisabled};
            display: flex;
            align-items: center;
            gap: 4px;
          `}
        >
          {isAI && (
            <span
              css={css`
                background: ${COLORS.cyanSoft};
                color: ${COLORS.cyan};
                border: 1px solid ${COLORS.borderStrong};
                border-radius: ${RADII.sm};
                padding: 0 4px;
                font-size: 0.5rem;
                font-weight: 700;
                letter-spacing: 0.08em;
              `}
            >
              AI
            </span>
          )}
          {participant.distance != null && `${participant.distance} м`}
          {participant.speaking && (
            <span
              css={css`
                color: ${COLORS.success};
                font-weight: 700;
              `}
            >
              ● говорит
            </span>
          )}
        </span>
      </div>

      {/* Ползунок громкости */}
      <input
        type='range'
        min={0}
        max={100}
        value={muted ? 0 : volume}
        onChange={event => onVolumeChange(Number(event.target.value))}
        aria-label={`Громкость ${participant.name}`}
        css={css`
          width: 48px;
          height: 4px;
          accent-color: ${COLORS.cyan};
          cursor: pointer;
        `}
      />

      {/* Мьют */}
      <button
        type='button'
        aria-label={muted ? `Включить ${participant.name}` : `Заглушить ${participant.name}`}
        aria-pressed={muted}
        onClick={onToggleMute}
        css={css`
          appearance: none;
          width: 28px;
          height: 28px;
          border-radius: ${RADII.sm};
          border: 1px solid ${muted ? COLORS.error + '66' : COLORS.border};
          background: ${muted ? COLORS.errorSoft : 'transparent'};
          color: ${muted ? COLORS.error : COLORS.textSecondary};
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background ${MOTION.hover}, color ${MOTION.hover};
          &:hover {
            background: ${COLORS.errorSoft};
            color: ${COLORS.error};
          }
        `}
      >
        {muted ? <VolumeXIcon size={ICON_SIZES.xs} /> : <Volume2Icon size={ICON_SIZES.xs} />}
      </button>

      {/* «…» — меню участника (заглушка, скоро) */}
      <button
        type='button'
        disabled
        aria-label='Действия с участником (скоро)'
        title='Действия с участником появятся скоро'
        css={css`
          appearance: none;
          width: 28px;
          height: 28px;
          border-radius: ${RADII.sm};
          border: 1px solid ${COLORS.border};
          background: transparent;
          color: ${COLORS.textDisabled};
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: not-allowed;
          opacity: 0.4;
        `}
      >
        <MoreHorizontalIcon size={ICON_SIZES.xs} />
      </button>
    </div>
  )
}

// ─── ТОСТ О НОВОМ СООБЩЕНИИ (слева снизу) ─────────────────────────────────────

function NewMessageToast({ msg, onJump, onDismiss }) {
  return (
    <div
      role='status'
      aria-live='polite'
      css={css`
        position: fixed;
        left: ${space(2)};
        bottom: ${space(3)};
        z-index: 60;
        max-width: 320px;
        background: ${COLORS.bgElevated};
        border: 1px solid ${COLORS.borderStrong};
        border-radius: ${RADII.xl};
        box-shadow: ${SHADOWS.panel};
        backdrop-filter: ${GLASS.base};
        display: flex;
        align-items: center;
        gap: ${space(1)};
        padding: ${space(1)} ${space(1)};
        animation: toastIn ${MOTION.enter} both;

        @keyframes toastIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}
    >
      {/* Аватар */}
      <div
        aria-hidden='true'
        css={css`
          flex: 0 0 auto;
          width: 28px;
          height: 28px;
          border-radius: ${RADII.round};
          background: ${avatarColor(msg.from || '')}22;
          border: 1.5px solid ${avatarColor(msg.from || '')}88;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.55rem;
          font-weight: 700;
          color: ${avatarColor(msg.from || '')};
        `}
      >
        {nameInitials(msg.from || '?')}
      </div>

      {/* Текст */}
      <div
        css={css`
          flex: 1;
          min-width: 0;
        `}
      >
        <div
          css={css`
            font-size: 0.65rem;
            font-weight: 650;
            color: ${COLORS.cyan};
          `}
        >
          {msg.from || 'Сообщение'}
        </div>
        <div
          css={css`
            font-size: ${TYPE.caption.size};
            color: ${COLORS.textSecondary};
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          `}
        >
          {msg.body}
        </div>
      </div>

      {/* Кнопка перейти */}
      <button
        type='button'
        onClick={onJump}
        css={css`
          appearance: none;
          background: ${COLORS.cyanSoft};
          border: 1px solid ${COLORS.borderStrong};
          border-radius: ${RADII.md};
          color: ${COLORS.cyan};
          font: inherit;
          font-size: 0.6rem;
          font-weight: 650;
          letter-spacing: 0.1em;
          padding: 4px 8px;
          cursor: pointer;
          white-space: nowrap;
          transition: background ${MOTION.hover};
          &:hover {
            background: ${COLORS.cyan};
            color: ${COLORS.bgBase};
          }
        `}
      >
        ↓ Перейти
      </button>

      {/* Закрыть */}
      <button
        type='button'
        aria-label='Закрыть уведомление'
        onClick={onDismiss}
        css={css`
          appearance: none;
          background: transparent;
          border: none;
          color: ${COLORS.textDisabled};
          cursor: pointer;
          padding: 2px;
          display: flex;
          align-items: center;
          &:hover { color: ${COLORS.text}; }
        `}
      >
        <XIcon size={12} />
      </button>
    </div>
  )
}

// ─── СВЁРНУТЫЙ РЕЖИМ (правый нижний угол) ─────────────────────────────────────

function CollapsedChat({ status, unread, voice, world, onExpand, onClose }) {
  return (
    <div
      role='complementary'
      aria-label='Свёрнутый чат'
      css={css`
        position: fixed;
        right: ${space(2)};
        bottom: ${space(2)};
        z-index: 50;
        background: ${COLORS.bgPanel};
        border: 1px solid ${COLORS.border};
        border-radius: ${RADII.xl};
        box-shadow: ${SHADOWS.panel};
        backdrop-filter: ${GLASS.base};
        display: flex;
        align-items: center;
        gap: ${space(1)};
        padding: ${space(1)} ${space(1)};
        min-height: ${TOUCH_MIN}px;
      `}
    >
      {/* Иконка чата */}
      <MessageSquareTextIcon size={ICON_SIZES.sm} color={COLORS.cyan} />

      {/* Место и люди */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 1px;
        `}
      >
        <span
          css={css`
            font-size: 0.65rem;
            font-weight: 650;
            color: ${COLORS.text};
            letter-spacing: 0.06em;
          `}
        >
          {status.place}
        </span>
        <span
          css={css`
            font-size: 0.58rem;
            color: ${COLORS.textSecondary};
          `}
        >
          {status.online} онлайн
        </span>
      </div>

      {/* Счётчик непрочитанных */}
      {unread > 0 && (
        <span
          css={css`
            background: ${COLORS.cyan};
            color: ${COLORS.bgBase};
            font-size: 0.6rem;
            font-weight: 700;
            padding: 1px 6px;
            border-radius: ${RADII.round};
            min-width: 18px;
            text-align: center;
          `}
        >
          {unread > 99 ? '99+' : unread}
        </span>
      )}

      {/* Кнопка развернуть */}
      <button
        type='button'
        aria-label='Развернуть чат'
        onClick={onExpand}
        css={css`
          appearance: none;
          width: ${TOUCH_MIN}px;
          height: ${TOUCH_MIN}px;
          border-radius: ${RADII.md};
          border: 1px solid ${COLORS.borderStrong};
          background: ${COLORS.cyanSoft};
          color: ${COLORS.cyan};
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background ${MOTION.hover};
          &:hover {
            background: ${COLORS.cyan};
            color: ${COLORS.bgBase};
          }
        `}
      >
        <ChevronRightIcon size={ICON_SIZES.xs} />
      </button>

      {/* Кнопка микрофона */}
      {voice.available && (
        <button
          type='button'
          aria-label={voice.muted ? 'Включить микрофон' : 'Выключить микрофон'}
          onClick={() => world.livekit.setMicrophoneEnabled()}
          css={css`
            appearance: none;
            width: ${TOUCH_MIN}px;
            height: ${TOUCH_MIN}px;
            border-radius: ${RADII.md};
            border: 1px solid ${COLORS.border};
            background: transparent;
            color: ${voice.muted ? COLORS.error : COLORS.textSecondary};
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: color ${MOTION.hover};
            &:hover { color: ${COLORS.text}; }
          `}
        >
          {voice.muted ? <MicOffIcon size={ICON_SIZES.xs} /> : <MicIcon size={ICON_SIZES.xs} />}
        </button>
      )}

      {/* Закрыть полностью */}
      <button
        type='button'
        aria-label='Закрыть чат'
        onClick={onClose}
        css={css`
          appearance: none;
          width: 28px;
          height: 28px;
          border-radius: ${RADII.sm};
          border: none;
          background: transparent;
          color: ${COLORS.textDisabled};
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          &:hover { color: ${COLORS.error}; }
        `}
      >
        <XIcon size={12} />
      </button>
    </div>
  )
}
